import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Store } from "lucide-react";
import type { Dashboard } from "@cd/core";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { plural } from "../marketplace/ListingCard";

/**
 * Delete a dashboard you own. Copies other people installed from its marketplace listing are
 * theirs and stay; the listing itself stays up unless you choose to remove it too.
 */
export function DeleteDashboardDialog({
  open, onOpenChange, dashboardId, title, widgetIds, shareCount,
}: {
  open: boolean; onOpenChange: (o: boolean) => void; dashboardId: string; title: string; widgetIds: string[]; shareCount: number;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const mine = useQuery({ queryKey: ["marketplace-mine"], queryFn: api.myListings, enabled: open });
  const listing = mine.data?.find((l) => l.kind === "dashboard" && l.sourceId === dashboardId);
  const widgetListings = mine.data?.filter((l) => l.kind === "widget" && l.sourceId && widgetIds.includes(l.sourceId)) ?? [];
  const [unpublish, setUnpublish] = useState(false);

  const remove = useMutation({
    mutationFn: async () => {
      if (listing && unpublish) await api.unpublish(listing.id);
      await api.deleteDashboard(dashboardId);
    },
    onSuccess: () => {
      onOpenChange(false);
      // Drop it from the cache first, so Home doesn't land straight back on it.
      qc.setQueryData<Dashboard[]>(["dashboards"], (ds) => ds?.filter((d) => d.id !== dashboardId));
      qc.removeQueries({ queryKey: ["dashboard", dashboardId] });
      qc.invalidateQueries({ queryKey: ["dashboards"] });
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["marketplace-mine"] });
      void navigate({ to: "/", replace: true });
    },
  });

  useEffect(() => {
    if (!open) return;
    setUnpublish(false);
    remove.reset();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Delete ${title}`} className="w-[480px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Delete “{title}”?</h2>
      <div className="mt-2 space-y-3 text-sm text-muted">
        <p>
          This can't be undone.
          {shareCount > 0 && " Everyone you've shared it with will lose access."}
        </p>
        {listing && (
          <div className="rounded-lg bg-warn-bg px-3 py-2.5 text-warn-fg">
            <p className="flex gap-1.5 font-medium"><Store className="mt-0.5 size-3.5 shrink-0" /> It's published to the marketplace</p>
            <p className="mt-1">
              “{listing.title}” (v{listing.version}, {plural(listing.installers, "installer")}). Copies people already installed are theirs and stay in their
              accounts either way.
            </p>
            <label className="mt-2 flex items-start gap-2 text-foreground">
              <input type="checkbox" checked={unpublish} onChange={(e) => setUnpublish(e.target.checked)} className="mt-0.5" />
              <span>Also remove the listing from the marketplace</span>
            </label>
            {!unpublish && <p className="mt-1 text-xs">The listing stays up at v{listing.version}, but you won't be able to publish updates to it.</p>}
          </div>
        )}
        {widgetListings.length > 0 && (
          <p>
            {widgetListings.length === 1 ? `The widget “${widgetListings[0]!.title}” is` : `${widgetListings.length} widgets on it are`} published on
            their own; {widgetListings.length === 1 ? "that listing stays" : "those listings stay"} up.
          </p>
        )}
      </div>
      {remove.error && <p className="mt-4 text-sm text-bad">{remove.error.message}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="danger" disabled={remove.isPending || mine.isLoading} onClick={() => remove.mutate()}>
          {listing && unpublish ? "Delete and unpublish" : "Delete dashboard"}
        </Button>
      </div>
    </Dialog>
  );
}
